import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews } from "@/utils/content";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
  index?: number;
}

export default function ArticleCard({ article, variant = "default", index }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0 cursor-pointer"
      >
        {typeof index === "number" ? (
          <span className="w-5 h-5 shrink-0 flex items-center justify-center font-heading font-black text-sm text-accent-500">
            {index + 1}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-accent-700 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span className="text-secondary-700 font-medium">{article.categoryName}</span>
            <span aria-hidden="true">·</span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 py-5 border-b border-background-200 cursor-pointer">
        <Link
          to={`/article/${article.slug}`}
          className="w-full sm:w-52 h-40 sm:h-32 shrink-0 overflow-hidden rounded-lg bg-background-200"
        >
          <img
            src={article.cover}
            alt={`${article.title} - 幕后拍摄现场报道配图`}
            title={`${article.title} 幕后花絮`}
            className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <Link
              to={`/category/${article.categorySlug}`}
              className="text-xs font-bold px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
            >
              {article.categoryName}
            </Link>
            {article.isHot ? (
              <span className="text-xs font-bold px-2 py-0.5 rounded bg-accent-100 text-accent-800">
                热
              </span>
            ) : null}
          </div>
          <h3 className="font-heading text-lg font-bold text-foreground-950 leading-snug group-hover:text-accent-700 transition-colors">
            <Link to={`/article/${article.slug}`}>{article.title}</Link>
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-auto pt-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" />
              {formatViews(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-message-3-line w-3.5 h-3.5 flex items-center justify-center" />
              {article.comments}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-100 border border-background-200 rounded-lg overflow-hidden transition-all duration-300 hover:border-primary-400 hover:-translate-y-1">
      <Link
        to={`/article/${article.slug}`}
        className="block w-full h-48 overflow-hidden bg-background-200"
      >
        <img
          src={article.cover}
          alt={`${article.title} - ${article.categoryName}幕后内容配图`}
          title={`${article.title} 拍摄幕后`}
          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">
            {article.categoryName}
          </span>
          {article.isHot ? (
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-accent-500 text-background-50">
              热
            </span>
          ) : null}
        </div>
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-accent-700 transition-colors line-clamp-2">
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-background-200 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
            {article.readTime}
          </span>
          <span className="inline-flex items-center gap-1 ml-auto">
            <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center text-accent-500" />
            {article.heat}
          </span>
        </div>
      </div>
    </article>
  );
}